import { useEffect, useId, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import type { Project, ProjectPeriodLock } from "../../api/schemas";
import type { ActivityOption } from "../../domain/activities";
import { orderProjects } from "../../domain/projects";
import type { ProjectChoices } from "../../domain/projects";
import { formatClock } from "../../domain/duration";
import type { EpochSeconds, LocalDate } from "../../domain/time";
import { useI18n } from "../../shared/i18n/I18nContext";
import { cx } from "../../shared/ui/classNames";
import { Field, withOwnError } from "../../shared/ui/Field";
import { formatLocalDate, formatTime } from "../../shared/ui/format";
import styles from "../../shared/ui/ui.module.css";
import { ValidatedForm } from "../../shared/ui/ValidatedForm";
import { ActivityChoice } from "./ActivityChoice";
import overviewStyles from "./DayOverview.module.css";
import { splitBlocks } from "./model/daySegments";
import type { DaySegments } from "./model/daySegments";
import { isChangeLocked, isProjectLocked } from "./model/pastDays";

/** Locked project periods of the day and the way to a request (F-07, `PINV-P05`). */
export interface LockedPeriodHandling {
  /** Locks covering the day. */
  readonly locks: readonly ProjectPeriodLock[];
  /** Opens a request instead of recording a change of locked time. */
  readonly onRequestChange: (
    start: EpochSeconds,
    end: EpochSeconds,
    projectId: string | null,
  ) => void;
}

/** Properties of {@link DayOverview}. */
export interface DayOverviewProps {
  /** Local date of the working day. */
  readonly date: LocalDate;
  /** Time zone for the displayed times. */
  readonly timeZone: string;
  /** Segments of the day with their projects. */
  readonly segments: DaySegments;
  /** Selectable projects. */
  readonly projects: readonly Project[];
  /** Favorites and recently used projects. */
  readonly choices: ProjectChoices;
  /** Whether an attribution is being saved. */
  readonly busy: boolean;
  /**
   * Attributes a part of the working time; `null` removes the attribution. A new project starts
   * without an activity (T-184).
   */
  readonly onAssign: (
    start: EpochSeconds,
    end: EpochSeconds,
    projectId: string | null,
    activityId: string | null,
  ) => void;
  /** The activities that can be chosen for a project (T-184); without them none is offered. */
  readonly activitiesOf?: (projectId: string | null) => readonly ActivityOption[];
  /** Whether the requirement asks for an activity (a hint only, never blocking). */
  readonly requireActivity?: boolean;
  /** Closes the overview ("Später zuordnen"); never changes the working time. */
  readonly onClose: () => void;
  /** Locked periods; without them every change is recorded directly. */
  readonly locked?: LockedPeriodHandling;
  /** Moves the focus to the overview when it opens (an editor opened on request, UX-110). */
  readonly autoFocus?: boolean;
  /** Further content above the closing button, for example a request form. */
  readonly children?: ReactNode;
}

/**
 * Parses a local time on the day of a block into an instant between its start and end. The time
 * field delivers `HH:mm`; typed text may also use a dot or no separator (`12.00`, `1200`).
 *
 * @param value - The entered time (24-hour clock).
 * @param start - Start of the block.
 * @param end - End of the block.
 * @param timeZone - Time zone of the day.
 * @returns The instant, or `null` if it does not lie inside the block.
 */
export function cutAt(
  value: string,
  start: EpochSeconds,
  end: EpochSeconds,
  timeZone: string,
): EpochSeconds | null {
  const match = /^(\d{1,2})[:.]?(\d{2})$/u.exec(value.trim());

  if (match === null) {
    return null;
  }

  const wanted = `${String(Number(match[1])).padStart(2, "0")}:${match[2] ?? "00"}`;
  const clock = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  });

  // Walks the minutes of the block, so that the time zone and daylight saving time stay correct.
  for (let instant = start - (start % 60) + 60; instant < end; instant += 60) {
    if (clock.format(instant * 1000) === wanted) {
      return instant;
    }
  }

  return null;
}

/**
 * Day overview after clocking out, for an open day (T-125) or for any past day (F-07, spec 6.5.4):
 * the parts of the working time with their projects, assignable to one or several projects;
 * "Später zuordnen" is always possible. The overview only records attributions; it never changes
 * the working time. With {@link LockedPeriodHandling}, parts whose old or new project is locked
 * open a request instead (`PINV-P05`).
 *
 * @param props - Component properties.
 * @returns The overview.
 */
export function DayOverview(props: DayOverviewProps): JSX.Element {
  const { t, languageTag } = useI18n();
  const [cuts, setCuts] = useState<readonly EpochSeconds[]>([]);
  const [splitting, setSplitting] = useState<number | null>(null);
  const [splitTime, setSplitTime] = useState("");
  const [splitInvalid, setSplitInvalid] = useState(false);
  const idPrefix = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const ordered = orderProjects(props.projects, props.choices);
  const time = (instant: EpochSeconds): string => formatTime(instant, languageTag, props.timeZone);
  const blocks = splitBlocks(props.segments.blocks, cuts).filter((block) => block.kind === "Work");
  const autoFocus = props.autoFocus === true;
  const activitiesFor = (projectId: string | null): readonly ActivityOption[] =>
    props.activitiesOf === undefined || projectId === null ? [] : props.activitiesOf(projectId);

  useEffect(() => {
    if (autoFocus) {
      heading.current?.focus();
    }
  }, [autoFocus]);

  const overview = (
    <section
      className={cx(styles["card"], styles["stack"])}
      aria-label={t("dayOverview.title", { date: formatLocalDate(props.date, languageTag) })}
    >
      <h2 ref={heading} tabIndex={-1}>
        {t("dayOverview.title", { date: formatLocalDate(props.date, languageTag) })}
      </h2>
      <p>
        {t("dayOverview.summary", {
          work: formatClock(props.segments.workSeconds),
          open: formatClock(props.segments.unattributedSeconds),
        })}
      </p>
      <ul className={overviewStyles["segments"]} aria-label={t("dayOverview.segments")}>
        {blocks.map((block, index) => {
          const selectId = `${idPrefix}-project-${String(index)}`;
          const splitErrorId = `${idPrefix}-split-${String(index)}-error`;
          const range = t("dayOverview.range", { from: time(block.start), to: time(block.end) });
          const locked = props.locked;
          const segmentLocked =
            locked !== undefined && isProjectLocked(locked.locks, block.projectId);
          const item = (
            <li
              key={`${String(block.start)}-${String(block.end)}`}
              className={overviewStyles["segment"]}
            >
              <div className={styles["field"]}>
                <label htmlFor={selectId}>
                  {range} ({formatClock(block.end - block.start)})
                </label>
                <select
                  id={selectId}
                  value={block.projectId ?? ""}
                  disabled={props.busy || segmentLocked}
                  onChange={(event) => {
                    const wanted = event.target.value === "" ? null : event.target.value;

                    if (
                      locked !== undefined &&
                      isChangeLocked(locked.locks, block.projectId, wanted)
                    ) {
                      locked.onRequestChange(block.start, block.end, wanted);
                      return;
                    }

                    props.onAssign(block.start, block.end, wanted, null);
                  }}
                >
                  <option value="">{t("project.none")}</option>
                  {ordered.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.code} · {project.name}
                    </option>
                  ))}
                  {block.projectId !== null &&
                  !props.projects.some((project) => project.id === block.projectId) ? (
                    <option value={block.projectId}>{t("dayOverview.unknownProject")}</option>
                  ) : null}
                </select>
              </div>
              {activitiesFor(block.projectId).length === 0 ? null : (
                <ActivityChoice
                  label={t("activity.ofSegment", { range })}
                  activities={activitiesFor(block.projectId)}
                  value={block.activityId ?? null}
                  disabled={props.busy || segmentLocked}
                  requireActivity={props.requireActivity === true}
                  onChange={(activityId) => {
                    props.onAssign(block.start, block.end, block.projectId, activityId);
                  }}
                />
              )}
              {locked !== undefined && segmentLocked ? (
                <div className={styles["row"]}>
                  <span className={styles["badge"]}>{t("dayOverview.lockedSegment")}</span>
                  <button
                    type="button"
                    className={styles["button"]}
                    aria-label={t("dayOverview.requestNamed", { range })}
                    onClick={() => {
                      locked.onRequestChange(block.start, block.end, block.projectId);
                    }}
                  >
                    {t("dayOverview.request")}
                  </button>
                </div>
              ) : null}
              {splitting === index ? (
                <ValidatedForm
                  className={styles["row"]}
                  onSubmit={(event) => {
                    event.preventDefault();
                    const cut = cutAt(splitTime, block.start, block.end, props.timeZone);

                    if (cut === null) {
                      setSplitInvalid(true);
                      return;
                    }

                    setCuts([...cuts, cut]);
                    setSplitting(null);
                    setSplitTime("");
                    setSplitInvalid(false);
                  }}
                >
                  <Field label={t("dayOverview.splitAt")}>
                    {(control) => (
                      <input
                        {...withOwnError(control, splitInvalid ? splitErrorId : null)}
                        type="time"
                        value={splitTime}
                        onChange={(event) => {
                          setSplitTime(event.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <button type="submit" className={styles["button"]}>
                    {t("dayOverview.splitConfirm")}
                  </button>
                </ValidatedForm>
              ) : block.end - block.start > 60 ? (
                <button
                  type="button"
                  className={styles["button"]}
                  aria-label={t("dayOverview.split", { range })}
                  onClick={() => {
                    setSplitting(index);
                    setSplitInvalid(false);
                  }}
                >
                  {t("dayOverview.splitShort")}
                </button>
              ) : null}
              {splitting === index && splitInvalid ? (
                <p id={splitErrorId} className={styles["muted"]} role="alert">
                  {t("dayOverview.splitInvalid", { from: time(block.start), to: time(block.end) })}
                </p>
              ) : null}
            </li>
          );
          return item;
        })}
      </ul>
      {props.children}
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onClose}>
          {props.segments.unattributedSeconds > 0 ? t("dayOverview.later") : t("dayOverview.done")}
        </button>
      </div>
    </section>
  );

  return overview;
}
